import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_CATEGORY_OPTIONS } from "./config";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../D1StaticData/edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { useTranslation } from "react-i18next";

const filterD1TransactionsSchema = z.object({
  d1_portfolio_client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  d1_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  d1_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  d1_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  d1_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  d1_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

const filterD1TransactionsFields = filterD1TransactionsSchema.keyof().enum;

export type FilterD1TransactionsSchema = z.infer<
  typeof filterD1TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterD1TransactionsSchema>[] = [
  {
    name: filterD1TransactionsFields.d1_portfolio_bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterD1TransactionsFields.d1_portfolio_client_contains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterD1TransactionsFields.d1_portfolio_vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterD1TransactionsFields.d1_portfolio_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterD1TransactionsFields.d1_portfolio_account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterD1Transactions() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.D1_TRANSACTIONS;

  const fields: Field[] = [
    {
      name: filterD1TransactionsFields.d1_portfolio_client_contains,
      label: t(`${tableName}.searchbox.d1_portfolio_client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterD1TransactionsSchema>(
        portfolioDepFields,
        filterD1TransactionsFields.d1_portfolio_client_contains,
      ),
    },
    {
      name: filterD1TransactionsFields.d1_portfolio_vehicle_contains,
      label: t(`${tableName}.searchbox.d1_portfolio_vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterD1TransactionsSchema>(
        portfolioDepFields,
        filterD1TransactionsFields.d1_portfolio_vehicle_contains,
      ),
    },
    {
      name: filterD1TransactionsFields.d1_portfolio_account_name_contains,
      label: t(`${tableName}.searchbox.d1_portfolio_account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterD1TransactionsSchema>(
        portfolioDepFields,
        filterD1TransactionsFields.d1_portfolio_account_name_contains,
      ),
    },
    {
      name: filterD1TransactionsFields.d1_portfolio_name_contains,
      label: t(`${tableName}.searchbox.d1_portfolio_name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterD1TransactionsSchema>(
        portfolioDepFields,
        filterD1TransactionsFields.d1_portfolio_name_contains,
      ),
    },
    {
      name: filterD1TransactionsFields.d1_portfolio_bank_name_contains,
      label: t(`${tableName}.searchbox.d1_portfolio_bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterD1TransactionsSchema>(
        portfolioDepFields,
        filterD1TransactionsFields.d1_portfolio_bank_name_contains,
      ),
    },
    {
      name: filterD1TransactionsFields.actif_contains,
      label: t(`${tableName}.searchbox.actif_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.D1}/${Tables.D1_STATIC_DATA}`,
      column_name: "name",
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterD1TransactionsFields.asset_class_1_equals,
      assetSubclassFieldName: filterD1TransactionsFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.D1_TRANSACTIONS}.searchbox.asset_class_1_equals`),
      assetSubclassLabel: t(`${Tables.D1_TRANSACTIONS}.searchbox.asset_class_2_equals`),
      client: Clients.D1,
      staticDataTableName: Tables.D1_STATIC_DATA,
      language: "fr",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterD1TransactionsFields.category_contains,
      label: t(`${tableName}.searchbox.category_contains`, { defaultValue: "Transaction type" }),
      type: "sectionSelect",
      useTranslation: true,
      section_name_translation_prefix: "transactions.category_groups",
      translate_key: "category",
      client: Clients.D1,
      table_name: Tables.D1_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      options: DEFAULT_CATEGORY_OPTIONS,
    },
    {
      name: filterD1TransactionsFields.transfer_contains,
      label: t(`${tableName}.searchbox.transfer_contains`, { defaultValue: "Transfer type" }),
      type: "select",
      useTranslation: true,
      client: Clients.D1,
      table_name: Tables.D1_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "transfer",
    },
    {
      name: filterD1TransactionsFields.fee_type_contains,
      label: t(`${tableName}.searchbox.fee_type_contains`, { defaultValue: "Fee type" }),
      type: "select",
      useTranslation: true,
      client: Clients.D1,
      table_name: Tables.D1_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "fee_type",
    },
    {
      name: filterD1TransactionsFields.description_contains,
      label: t(`${tableName}.searchbox.description_contains`, { defaultValue: "Description" }),
      type: "input",
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterD1TransactionsFields.date_from },
      to: { name: filterD1TransactionsFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterD1TransactionsFields.create_from },
      to: { name: filterD1TransactionsFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={Clients.D1}
      schema={filterD1TransactionsSchema}
      tableName={Tables.D1_TRANSACTIONS}
      fields={fields}
    />
  );
}
